import {
  getConfigurationValidationErrors,
  getConfigurationRules,
  getInvestorRatesUsed,
  getProducts,
  getTerseConfiguration,
  getAudiences,
} from '@pe/services/configurations.js';
import { getRates } from '@pe/services/rates.js';
import { ConfigurationStatuses, PERuleCategories } from '@shared/constants';
import { onErrorHandler } from '@shared/utils/errorHandlers.js';
import {
  SET_BASE_CONFIGURATION,
  SET_CONFIGURATION_NEIGHBORS,
  SET_INVESTOR_RATES_USED,
  SET_INVESTOR_UPDATES,
  SET_IS_TERSE_LOADING,
  SET_RATES,
  SET_ELIGIBILITY_MATRIX_RULES,
  SET_TERSE_CONFIGURATION,
  SET_AUDIENCES,
  SET_IS_AUDIENCES_LOADING,
} from './mutationTypes.js';

export default {
  namespaced: true,
  state: {
    terseConfiguration: { id: '', name: '', status: '' },
    configurationNeighbors: null,
    baseConfiguration: null,
    rates: null,
    eligibilityMatrixRules: null,
    investorRatesUsed: { isLoading: true, data: null },
    isTerseLoading: true,
    investorUpdates: {},
    audiences: [],
    isAudiencesLoading: false,
  },
  getters: {
    baseConfiguration: state => state.baseConfiguration,
    configurationName: state => state.terseConfiguration?.name,
    configurationNeighbors: state => state.configurationNeighbors,
    investorRatesUsed: state => state.investorRatesUsed,
    isConfigurationReadOnly: state =>
      state.terseConfiguration?.id &&
      state.terseConfiguration?.status !== ConfigurationStatuses.DRAFT,
    isTerseLoading: state => state.isTerseLoading,
    rates: state => state.rates,
    eligibilityMatrixRules: state => state.eligibilityMatrixRules,
    terseConfiguration: state => state.terseConfiguration,
    investorUpdates: state => state.investorUpdates,
    audiences: state => state.audiences,
    isAudiencesLoading: state => state.isAudiencesLoading,
    audienceIdToName: state => {
      return state.audiences.reduce((acc, audience) => {
        acc[audience.id] = audience.name;
        return acc;
      }, {});
    },
  },
  mutations: {
    [SET_TERSE_CONFIGURATION](state, configuration) {
      state.terseConfiguration = configuration;
    },
    [SET_CONFIGURATION_NEIGHBORS](state, configurationNeighbors) {
      state.configurationNeighbors = configurationNeighbors;
    },
    [SET_BASE_CONFIGURATION](state, baseConfiguration) {
      state.baseConfiguration = baseConfiguration;
    },
    [SET_RATES](state, rates) {
      state.rates = rates;
    },
    [SET_ELIGIBILITY_MATRIX_RULES](state, rules) {
      state.eligibilityMatrixRules = rules;
    },
    [SET_INVESTOR_RATES_USED](state, investorRatesUsed) {
      state.investorRatesUsed = investorRatesUsed;
    },
    [SET_IS_TERSE_LOADING](state, isLoading) {
      state.isTerseLoading = isLoading;
    },
    [SET_INVESTOR_UPDATES](state, investorUpdates) {
      state.investorUpdates = investorUpdates;
    },
    [SET_AUDIENCES](state, audiences) {
      state.audiences = audiences;
    },
    [SET_IS_AUDIENCES_LOADING](state, isLoading) {
      state.isAudiencesLoading = isLoading;
    },
  },
  actions: {
    async clearTerseConfiguration({ commit }) {
      commit(SET_TERSE_CONFIGURATION, { id: '', name: '', status: '' });
    },
    async getTerseConfiguration(
      { commit, state },
      {
        id,
        force = false,
        includeBase = false,
        includeValidationErrors = true,
        includeProducts = false,
      },
    ) {
      commit(SET_IS_TERSE_LOADING, true);
      try {
        let configuration = state.terseConfiguration;
        const promises = [];

        let configPromise = Promise.resolve(configuration);
        if (force || configuration?.id !== id) {
          configPromise = getTerseConfiguration(id);
        }
        promises.push(configPromise);

        // Promise for base configuration (conditionally)
        let baseConfigPromise;
        if (includeBase) {
          // We need to wait for the main config to get the baseChangesetId
          baseConfigPromise = configPromise.then(config => {
            if (config?.baseChangesetId) {
              return getTerseConfiguration(config.baseChangesetId);
            }
            return null;
          });
          promises.push(baseConfigPromise);
        }

        let validationErrorsPromise;
        if (includeValidationErrors) {
          validationErrorsPromise = getConfigurationValidationErrors(id);
          promises.push(validationErrorsPromise);
        }

        let productsPromise;
        if (includeProducts) {
          productsPromise = getProducts(id);
          promises.push(productsPromise);
        }

        await Promise.all(promises);
        configuration = await configPromise;
        if (validationErrorsPromise) {
          configuration = {
            ...configuration,
            validationErrors: (await validationErrorsPromise)['items'],
          };
          Object.freeze(configuration.validationErrors);
        }

        if (includeProducts) {
          // A new object is created to ensure reactivity.
          configuration = {
            ...configuration,
            products: await productsPromise,
          };
        }

        if (baseConfigPromise) {
          commit(SET_BASE_CONFIGURATION, await baseConfigPromise);
        }
        commit(SET_TERSE_CONFIGURATION, configuration);
        return configuration;
      } catch (error) {
        onErrorHandler(error, 'get-terse-configuration');
      } finally {
        commit(SET_IS_TERSE_LOADING, false);
      }
    },
    async getCurrentRates({ commit }, params) {
      try {
        commit(SET_RATES, null);
        const tableOptions = params.tableOptions;
        const id = params.id;
        const rates = await getRates(tableOptions, id);
        commit(SET_RATES, rates);
      } catch (error) {
        onErrorHandler(error, 'pe-config-details');
      }
    },
    async getEligibilityMatrixRules({ commit }, configurationId) {
      try {
        commit(SET_ELIGIBILITY_MATRIX_RULES, null);
        const rules = await getConfigurationRules(
          configurationId,
          `category,${PERuleCategories.EligibilityMatrix}`,
          99999,
        );
        commit(SET_ELIGIBILITY_MATRIX_RULES, rules['entities']);
      } catch (error) {
        onErrorHandler(error, 'pe-config-details');
      }
    },
    async getInvestorRatesUsed({ commit }, configurationId) {
      try {
        commit(SET_INVESTOR_RATES_USED, { isLoading: true, data: null });
        const investorRatesUsed = await getInvestorRatesUsed(configurationId);
        commit(SET_INVESTOR_RATES_USED, {
          isLoading: false,
          data: investorRatesUsed,
        });
      } catch (error) {
        onErrorHandler(error, 'pe-config-details');
        commit(SET_INVESTOR_RATES_USED, { isLoading: false, data: null });
      }
    },
    setConfigurationNeighbors({ commit }, configurationNeighbors) {
      commit(SET_CONFIGURATION_NEIGHBORS, configurationNeighbors);
    },
    setInvestorUpdates({ commit }, investorUpdates) {
      commit(SET_INVESTOR_UPDATES, investorUpdates);
    },
    async fetchAudiences({ commit, state }, { configId, force = false }) {
      // Prevent concurrent requests
      if (state.isAudiencesLoading) {
        return state.audiences;
      }

      // Only fetch if not already loaded or if forced
      if (!force && state.audiences.length > 0) {
        return state.audiences;
      }

      if (!configId) {
        return [];
      }

      commit(SET_IS_AUDIENCES_LOADING, true);
      try {
        const audiences = await getAudiences(configId);
        commit(SET_AUDIENCES, audiences);
        return audiences;
      } catch (error) {
        onErrorHandler(error, 'pe-pricing-version-details-fetch-audiences');
        commit(SET_AUDIENCES, []);
        return [];
      } finally {
        commit(SET_IS_AUDIENCES_LOADING, false);
      }
    },
    clearAudiences({ commit }) {
      commit(SET_AUDIENCES, []);
    },
  },
};
